import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GridCellKind, type GridCell } from '@glideapps/glide-data-grid';
import type { PortfolioCompanySummary } from '@nemo/api';
import { dealroomCompanyUrl, matchesPortfolioFilters, timeSinceRaiseText } from '@nemo/core/model';
import { useSearchParams } from 'wouter';
import { chipsCell } from '../grid/cells/index.js';
import { csvOf, orderedColumns, sortRows, type GridColumnDef, type Sort } from '../grid/columns.js';
import { download, today } from '../grid/csv.js';
import { DataGrid } from '../grid/DataGrid.js';
import { DensityToggle } from '../grid/DensityToggle.js';
import { useGridPreferences } from '../grid/useGridPreferences.js';
import { useTRPC } from '../trpc.js';
import { MultiSelect } from '../ui/MultiSelect.js';
import { GRID_ICON_TEXT_PADDING } from '../ui/SiteIcon.js';
import { Select, type SelectOption } from '../ui/Select.js';
import { formatEur } from '../ui/format.js';
import {
  SOURCE_LABELS,
  coInvestorsText,
  employeesText,
  lastRaiseText,
  roundsText,
  sourcesText,
} from './format.js';
import { PortfolioPane } from './PortfolioPane.js';

const PREFERENCES_KEY = 'nemo.grid.portfolio.v1';

function textCell(text: string, options: { link?: boolean; icon?: boolean } = {}): GridCell {
  return {
    kind: GridCellKind.Text,
    data: text,
    displayData: text,
    allowOverlay: false,
    readonly: true,
    ...(options.link === true ? { cursor: 'pointer' } : {}),
    // Room for the site icon the grid paints into the cell's left edge.
    ...(options.icon === true
      ? { themeOverride: { cellHorizontalPadding: GRID_ICON_TEXT_PADDING } }
      : {}),
  };
}

const COLUMNS: readonly GridColumnDef<PortfolioCompanySummary>[] = [
  {
    id: 'name',
    title: 'Company',
    width: 220,
    cell: (company) => textCell(company.name, { link: true, icon: true }),
    text: (company) => company.name,
    sortKey: (company) => company.name.toLowerCase(),
  },
  {
    id: 'status',
    title: 'Status',
    width: 120,
    cell: (company) => textCell(company.status),
    text: (company) => company.status,
    sortKey: (company) => company.status,
  },
  {
    id: 'holdingFunds',
    title: 'Holding funds',
    width: 200,
    cell: (company) =>
      chipsCell(company.holdingFunds.map((code) => ({ label: code, tone: 'neutral' }))),
    text: (company) => company.holdingFunds.join(', '),
    sortKey: (company) => company.holdingFunds.join(', '),
  },
  {
    id: 'coInvestors',
    title: 'Co-investors',
    width: 280,
    cell: (company) => textCell(coInvestorsText(company)),
    text: (company) => company.coInvestors.map((row) => row.fundName).join('; '),
    sortKey: (company) => company.coInvestors.length,
  },
  {
    id: 'rounds',
    title: 'Rounds',
    width: 200,
    cell: (company) => textCell(roundsText(company)),
    text: (company) => roundsText(company),
    sortKey: (company) => company.latestRound?.announcedOn ?? '',
  },
  {
    id: 'sectorTags',
    title: 'Sectors',
    width: 200,
    cell: (company) =>
      chipsCell(company.sectorTags.map((tag) => ({ label: tag, tone: 'neutral' }))),
    text: (company) => company.sectorTags.join(', '),
    sortKey: (company) => company.sectorTags.join(', '),
  },
  {
    id: 'lastRaise',
    title: 'Last raise',
    width: 150,
    note: 'The newest of its rounds and the last round Dealroom names',
    cell: (company) => textCell(company.lastRaise === null ? '' : lastRaiseAgo(company)),
    // Unknown is a faint dash rather than a blank, so it reads as "nobody knows".
    faintSuffix: (company) => (company.lastRaise === null ? '—' : ''),
    text: (company) => lastRaiseText(company) ?? '',
    sortKey: (company) => company.lastRaise?.raisedOn ?? null,
  },
  {
    id: 'growthStage',
    title: 'Stage',
    width: 120,
    note: "Dealroom's growth stage",
    cell: (company) => textCell(company.growthStage ?? ''),
    text: (company) => company.growthStage ?? '',
    sortKey: (company) => company.growthStage,
  },
  {
    id: 'totalRaised',
    title: 'Total raised',
    width: 110,
    note: 'Everything it has raised, as Dealroom last said',
    cell: (company) =>
      textCell(company.totalFundingEur === null ? '' : formatEur(company.totalFundingEur)),
    text: (company) => (company.totalFundingEur === null ? '' : String(company.totalFundingEur)),
    sortKey: (company) => company.totalFundingEur,
  },
  {
    id: 'employees',
    title: 'Employees',
    width: 100,
    cell: (company) => textCell(employeesText(company) ?? ''),
    text: (company) => employeesText(company) ?? '',
    sortKey: (company) => company.employees,
  },
  {
    id: 'sources',
    title: 'Source',
    width: 260,
    cell: (company) =>
      chipsCell(
        company.sources.map((source) => ({ label: SOURCE_LABELS[source], tone: 'neutral' })),
      ),
    text: (company) => sourcesText(company),
    sortKey: (company) => sourcesText(company),
  },
  {
    id: 'dealroom',
    title: 'Dealroom',
    width: 110,
    cell: (company) =>
      textCell(dealroomCompanyUrl(company.dealroomPath) === null ? '' : 'Dealroom ↗', {
        link: true,
      }),
    text: (company) => dealroomCompanyUrl(company.dealroomPath) ?? '',
    sortKey: (company) => (company.dealroomPath === null ? 1 : 0),
    links: (company) => {
      const href = dealroomCompanyUrl(company.dealroomPath);
      return href === null ? [] : [{ label: 'Dealroom', href }];
    },
  },
];

/** "14 mo ago", with the round in the export rather than the cell. */
function lastRaiseAgo(company: PortfolioCompanySummary): string {
  return company.lastRaise === null ? '' : timeSinceRaiseText(company.lastRaise);
}

/**
 * The portfolio companies: the same grid, toolbar, density toggle and CSV as
 * the funds page, over Project A's own companies. Read-only here; a company
 * is changed in its pane.
 */
export function PortfolioGrid(): React.JSX.Element {
  const trpc = useTRPC();
  const companies = useQuery(trpc.portfolio.list.queryOptions());
  const projectAFunds = useQuery(trpc.projectAFunds.list.queryOptions());

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<readonly string[]>([]);
  const [fundFilter, setFundFilter] = useState<readonly string[]>([]);
  const [coInvestorFilter, setCoInvestorFilter] = useState('');
  const [sort, setSort] = useState<Sort | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const preferences = useGridPreferences(PREFERENCES_KEY);

  // `/portfolio?company=<id>` is a link to one company, as `/?fund=` is to a fund.
  const [searchParams] = useSearchParams();
  const linked = searchParams.get('company');
  useEffect(() => {
    if (linked !== null) setOpenId(linked);
  }, [linked]);

  const all = companies.data ?? [];
  const statusOptions: readonly SelectOption[] = useMemo(
    () =>
      [...new Set(all.map((company) => company.status))]
        .sort((left, right) => left.localeCompare(right))
        .map((status) => ({ value: status, label: status })),
    [all],
  );
  const fundOptions: readonly SelectOption[] = useMemo(
    () => (projectAFunds.data ?? []).map((fund) => ({ value: fund.code, label: fund.code })),
    [projectAFunds.data],
  );
  const coInvestorOptions: readonly SelectOption[] = useMemo(() => {
    const names = [
      ...new Set(all.flatMap((company) => company.coInvestors.map((row) => row.fundName))),
    ];
    return [
      { value: '', label: 'Any co-investor' },
      ...names
        .sort((left, right) => left.localeCompare(right))
        .map((name) => ({ value: name, label: name })),
    ];
  }, [all]);

  const rows = useMemo(
    () =>
      sortRows(
        all.filter((company) =>
          matchesPortfolioFilters(company, {
            status: statusFilter,
            holdingFunds: fundFilter,
            coInvestor: coInvestorFilter,
            text: search,
          }),
        ),
        COLUMNS,
        sort,
        undefined,
      ),
    [all, statusFilter, fundFilter, coInvestorFilter, search, sort],
  );

  const columns = useMemo(() => orderedColumns(COLUMNS, preferences), [preferences]);

  const closePane = useCallback(() => setOpenId(null), []);

  const exportCsv = useCallback(() => {
    download(`project-a-portfolio-${today()}.csv`, csvOf(rows, columns, undefined));
  }, [columns, rows]);

  if (companies.isPending) return <p className="subtle loading">Loading the portfolio…</p>;
  if (companies.isError) {
    return (
      <p className="subtle loading">Could not load the portfolio: {companies.error.message}</p>
    );
  }

  return (
    <>
      <div className="toolbar">
        <strong className="count">
          {rows.length} compan{rows.length === 1 ? 'y' : 'ies'}
        </strong>
        <input
          className="search"
          placeholder="Filter…"
          aria-label="Filter the companies"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <MultiSelect
          label="Status"
          noun="statuses"
          placeholder="Every status"
          options={statusOptions}
          selected={statusFilter}
          onToggle={(value) =>
            setStatusFilter((current) =>
              current.includes(value)
                ? current.filter((status) => status !== value)
                : [...current, value],
            )
          }
          onClear={() => setStatusFilter([])}
        />
        <MultiSelect
          label="Holding fund"
          noun="funds"
          placeholder="Every holding fund"
          options={fundOptions}
          selected={fundFilter}
          onToggle={(value) =>
            setFundFilter((current) =>
              current.includes(value)
                ? current.filter((code) => code !== value)
                : fundOptions
                    .map((option) => option.value)
                    .filter((code) => code === value || current.includes(code)),
            )
          }
          onClear={() => setFundFilter([])}
        />
        <Select
          label="Co-investor"
          value={coInvestorFilter}
          onChange={setCoInvestorFilter}
          options={coInvestorOptions}
        />
        <span className="spacer" />
        <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
        <button className="button primary" type="button" onClick={exportCsv}>
          Export CSV
        </button>
      </div>

      <DataGrid
        rows={rows}
        columns={columns}
        context={undefined}
        rowId={(company) => company.id}
        preferences={preferences}
        sort={{ value: sort, onChange: setSort }}
        siteIcon={{ columnId: 'name', of: (company) => company }}
        open={{ openId, onOpen: (company) => setOpenId(company.id), onClose: closePane }}
        tip={tipFor}
        freezeColumns={1}
        aside={(step) =>
          openId !== null && <PortfolioPane companyId={openId} onClose={closePane} onStep={step} />
        }
      />
    </>
  );
}

/** The whole list behind a "+N": every co-investor, each on its own line. */
function tipFor(
  company: PortfolioCompanySummary,
  column: GridColumnDef<PortfolioCompanySummary>,
): string | null {
  if (column.id === 'coInvestors' && company.coInvestors.length > 3) {
    return company.coInvestors.map((row) => row.fundName).join('\n');
  }
  return null;
}
